/**
 * Stacked 100% share chart — two stacks per bucket (SPEC `Content Performance` § Post Performance,
 * In vs Out of network).
 *
 * As dumb as `Chart`: the server ships each bucket's two shares *and* their rendered strings, and
 * this file turns the shares into column heights and nothing else. Percent geometry is chart
 * rendering, not client math (§12.5 rule 1) — no normalising, no summing, no filling. A bucket the
 * server could not compute (`inPct === null`) renders as an empty slot carrying the server's own
 * `inDisplay`, never as a 0% column.
 */

import { useState, type ReactNode } from 'react';
import { cx } from '../cx';
import './chart.css';

/** Structurally `ContentInOutBucketDTO`; kept local so `components/` owns no feature contract. */
export type StackedShareBucket = {
  label: string;
  fullLabel: string;
  /** `null` when the bucket has nothing to divide — renders empty, never as zero. */
  inPct: number | null;
  outPct: number | null;
  /** Rendered verbatim in the hover; `"—"` when the share is unknown. */
  inDisplay: string;
  outDisplay: string;
};

export type StackedShareChartProps = {
  buckets: readonly StackedShareBucket[];
  /** Legend + hover row names — static chrome, like a `<th>`. */
  inLabel: string;
  outLabel: string;
  inColor: string;
  outColor: string;
  height?: number;
  ariaLabel?: string;
  /** Appended to the hover body, e.g. the bucket's post count — server strings only. */
  renderExtra?: (index: number) => ReactNode;
};

export function StackedShareChart({
  buckets,
  inLabel,
  outLabel,
  inColor,
  outColor,
  height = 180,
  ariaLabel,
  renderExtra,
}: StackedShareChartProps) {
  const [hover, setHover] = useState<number | null>(null);

  const n = buckets.length;
  // Same rule as `Chart`'s x labels: thin them out rather than let them collide.
  const stride = Math.max(1, Math.ceil(n / 12));
  const hovered = hover === null ? undefined : buckets[hover];

  return (
    <div className="share-chart" role="img" aria-label={ariaLabel}>
      <div className="share-chart__plot" style={{ height }}>
        {buckets.map((bucket, index) => (
          <div
            key={`${bucket.label}-${String(index)}`}
            className={cx('share-col', hover !== null && hover !== index && 'is-dimmed')}
            onMouseEnter={() => setHover(index)}
            onMouseLeave={() => setHover(null)}
          >
            <div className="share-col__stack">
              {bucket.inPct === null || bucket.outPct === null ? (
                <div className="share-seg share-seg--empty" />
              ) : (
                <>
                  <div
                    className="share-seg"
                    style={{ height: `${String(bucket.outPct)}%`, background: outColor }}
                  />
                  <div
                    className="share-seg"
                    style={{ height: `${String(bucket.inPct)}%`, background: inColor }}
                  />
                </>
              )}
            </div>
          </div>
        ))}

        {hovered !== undefined ? (
          <div className="chart-tooltip visible share-chart__tip">
            <div className="t-label">{hovered.fullLabel}</div>
            <div className="t-row">
              <span className="t-label">
                <span className="t-dot" style={{ background: inColor }} aria-hidden />
                {inLabel}
              </span>
              <span className="t-value">{hovered.inDisplay}</span>
            </div>
            <div className="t-row">
              <span className="t-label">
                <span className="t-dot" style={{ background: outColor }} aria-hidden />
                {outLabel}
              </span>
              <span className="t-value">{hovered.outDisplay}</span>
            </div>
            {hover !== null ? renderExtra?.(hover) : null}
          </div>
        ) : null}
      </div>

      <div className="share-chart__axis">
        {buckets.map((bucket, index) => (
          <span key={`l-${bucket.label}-${String(index)}`} className="share-chart__label">
            {index % stride === 0 || index === n - 1 ? bucket.label : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

export default StackedShareChart;
